import React from "react";
import { useQuery } from "@tanstack/react-query";
import apiAgent from "../../../../app/api/apiAgent";
import { RequirementModel } from "../../../../app/models/RequirementModel";
import { Filter } from "lucide-react";
import LoadingComponent from "@/app/layout/LoadingComponent";
import NewLoader from "@/app/layout/NewLoader";
import { ToPriceStringWithoutDecimal } from "../../../../app/common/common-functions/functions";

interface RequirementsListProps {
  contactID: string;
}

// Function to format requirement text similar to RequirementComponent
const getRequirementText = (item: RequirementModel) => {
  let infoText = "";
  if (item.saleType == "1") infoText = "For sale";
  else if (item.saleType == "2") infoText = "For rent";
  else if (item.saleType == "3") infoText = "Auction";
  else infoText = "Valuation";

  if (item.priceMin && item.priceMax) {
    infoText = infoText + " | " + ToPriceStringWithoutDecimal(item.priceMin) + " to " + ToPriceStringWithoutDecimal(item.priceMax);
  } else if (item.priceMin) {
    infoText = infoText + " | min " + ToPriceStringWithoutDecimal(item.priceMin);
  } else if (item.priceMax) {
    infoText = infoText + " | max " + ToPriceStringWithoutDecimal(item.priceMax);
  }

  if (item.beds) infoText = infoText + " | " + item.beds + "+ Beds";

  if (item.baths) infoText = infoText + " | " + item.baths + "+ Baths";

  if (item.garages) infoText = infoText + " | " + item.garages + "+ Garages";

  if (item.parkingBays)
    infoText = infoText + " | " + item.parkingBays + "+ Parking bays";

  if (item.floorSizeMin)
    infoText =
      infoText +
      " | " +
      item.floorSizeMin +
      (item.floorSizeUnit == "1"
        ? "m²"
        : item.floorSizeUnit == "2"
          ? "ft²"
          : "ht") +
      "+ Floor size";

  if (item.landSizeMin)
    infoText =
      infoText +
      " | " +
      item.landSizeMin +
      (item.landSizeUnit == "1"
        ? "m²"
        : item.landSizeUnit == "2"
          ? "ft²"
          : "ht") +
      "+ Land size";

  let selectedAreasString = "";
  if (item.selectedAreas && item.selectedAreas.length > 0) {
    for (let i = 0; i < item.selectedAreas.length; i++) {
      if (i == 0) selectedAreasString = item.selectedAreas[i];
      else
        selectedAreasString =
          selectedAreasString + "; " + item.selectedAreas[i];
    }
  }

  if (selectedAreasString) infoText = infoText + " | " + selectedAreasString;

  return infoText;
};

function RequirementsList({ contactID }: RequirementsListProps) {
  const { data: requirements, status } = useQuery({
    queryKey: ["requirements", contactID],
    queryFn: () => apiAgent.Contact.requirementsAjax(contactID),
    enabled: !!contactID,
  });

  return (
    <div className="tw-flex tw-gap-2">
      <Filter className="tw-size-6 tw-fill-foreground tw-text-foreground" />
      {/* Requirements/Qualifications */}
      {status === "success" &&
        requirements &&
        Array.isArray(requirements) &&
        requirements.length !== 0 && (
          <div className="tw-flex tw-flex-col tw-gap-2">
            {requirements?.map(
              (requirement: RequirementModel, index: number) => {
                const noteString =
                  requirement.note.length > 45
                    ? requirement.note.substring(0, 45) + ".."
                    : requirement.note;

                return (
                  <div key={index} className="tw-text-sm">
                    <span className="tw-font-semibold">{noteString}</span>:{" "}
                    {getRequirementText(requirement)}
                  </div>
                );
              }
            )}
          </div>
        )}
      {status === "success" &&
        (!requirements ||
          (Array.isArray(requirements) && requirements.length === 0)) && (
          <div className="tw-text-sm tw-text-gray-500">
            No qualifications found
          </div>
        )}
      {status === "error" && (
        <div className="tw-text-sm tw-text-red-500">
          Error loading qualifications
        </div>
      )}
      {status === "pending" && <NewLoader iconOnly />}
    </div>
  );
}

export default RequirementsList;
